import { Controller } from "@hotwired/stimulus"
import { selectElement, selectElements, dispatchElement, handleElement } from "../shared/domHelper"

/**
 * MakaseController - data-action의 확장 기능을 제공하는 Stimulus 컨트롤러
 *
 * 사용법:
 * <div data-controller="makase">
 *   <button data-makase-action="
 *     click->{ alert(this.dataset.message, event) }
 *   " data-message="Example">버튼</button>
 * </div>
 *
 * 문법:
 * - 인라인 JavaScript (멀티라인 지원): eventname->{ ... code ... }
 * - 컨트롤러 액션 지원하지 않음 (오로지 이벤트와 JavaScript eval만)
 * - return false 같은 구문 지원하지 않음
 *
 * 사용 가능한 숏컷 변수:
 * - event, $event: 이벤트 객체
 * - $el: 이벤트 currentTarget 요소
 * - value, $value: input/select/textarea의 value (해당 요소만)
 * - controller, $controller: makase 컨트롤러 인스턴스
 *
 * 헬퍼 함수:
 * - $select(selector, callback): selectElement 래퍼
 * - $selects(selector, callback): selectElements 래퍼
 * - $emit(eventName, detail, bubbles): 컨트롤러 엘리먼트에서 이벤트 발생 (petite-vue $emit과 동일)
 * - $dispatch(selector, eventName, detail, bubbles): dispatchElement 래퍼
 * - $handle(selector, eventName, detail): handleElement 래퍼 (bubbles=false)
 * - $ref(refArg, callback): data-makase-ref 요소 선택 (단일)
 *     - $ref('target') -> [controller.element, '[data-makase-ref~=target]']
 *     - $ref([el, 'li', 'target']) -> [controller.element, el, 'li', '[data-makase-ref~=target]']
 *     - $ref(element) -> data-makase-ref가 있으면 해당 요소
 * - $refs(refArg, callback): data-makase-ref 요소 선택 (복수)
 *
 * 특징:
 * - data-makase-action 변경 시 이벤트 핸들러 자동 갱신
 * - 자식 요소의 data-makase-action 동적 추가/삭제 감지
 * - 중첩된 data-controller="makase" 스코프 분리
 * - 멀티라인 코드 블록 지원
 */
export default class MakaseController extends Controller {
  connect() {
    // 현재 컨트롤러가 관리하는 요소들의 핸들러 맵
    // Map<Element, Map<string, Function[]>>
    this.handlerMap = new Map()

    // 초기 바인딩
    this.bindAllActions()

    // MutationObserver로 DOM 변경 감지
    this.observer = new MutationObserver(this.handleMutations.bind(this))
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-makase-action"],
    })
  }

  disconnect() {
    // 모든 핸들러 해제
    this.unbindAllActions()

    // Observer 해제
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  /**
   * DOM 변경 감지 핸들러
   */
  handleMutations(mutations) {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        // data-makase-action 속성 변경 감지
        const element = mutation.target
        if (this.isInMyScope(element)) {
          this.rebindElement(element)
        }
      } else if (mutation.type === "childList") {
        // 추가된 노드 처리
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.bindNewElements(node)
          }
        }
        // 삭제된 노드 처리
        for (const node of mutation.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.unbindRemovedElements(node)
          }
        }
      }
    }
  }

  /**
   * 요소가 현재 컨트롤러의 스코프 내에 있는지 확인
   * (중첩된 makase 컨트롤러 제외)
   */
  isInMyScope(element) {
    // 자기 자신은 포함
    if (element === this.element) return true

    // 자식 요소인지 확인
    if (!this.element.contains(element)) return false

    // 중첩된 makase 컨트롤러가 있는지 확인
    let parent = element.parentElement
    while (parent && parent !== this.element) {
      const controllers = parent.dataset.controller?.split(/\s+/) || []
      if (controllers.includes("makase")) {
        // 다른 makase 컨트롤러의 스코프 내에 있음
        return false
      }
      parent = parent.parentElement
    }

    return true
  }

  /**
   * 스코프 내의 모든 data-makase-action 요소 수집
   */
  collectActionElements() {
    const elements = []

    // 컨트롤러 요소 자체도 확인
    if (this.element.hasAttribute("data-makase-action")) {
      elements.push(this.element)
    }

    // 자식 요소들 수집
    const candidates = this.element.querySelectorAll("[data-makase-action]")
    for (const el of candidates) {
      if (this.isInMyScope(el)) {
        elements.push(el)
      }
    }

    return elements
  }

  /**
   * 모든 액션 바인딩
   */
  bindAllActions() {
    const elements = this.collectActionElements()
    for (const el of elements) {
      this.bindElement(el)
    }
  }

  /**
   * 모든 액션 해제
   */
  unbindAllActions() {
    for (const [element, handlersMap] of this.handlerMap) {
      for (const [eventType, handlers] of handlersMap) {
        for (const handler of handlers) {
          element.removeEventListener(eventType, handler)
        }
      }
    }
    this.handlerMap.clear()
  }

  /**
   * 새로 추가된 요소들 바인딩
   */
  bindNewElements(root) {
    if (root.hasAttribute?.("data-makase-action") && this.isInMyScope(root)) {
      this.bindElement(root)
    }

    const descendants = root.querySelectorAll?.("[data-makase-action]") || []
    for (const el of descendants) {
      if (this.isInMyScope(el)) {
        this.bindElement(el)
      }
    }
  }

  /**
   * 삭제된 요소들 언바인딩
   */
  unbindRemovedElements(root) {
    this.unbindElement(root)

    const descendants = root.querySelectorAll?.("[data-makase-action]") || []
    for (const el of descendants) {
      this.unbindElement(el)
    }
  }

  /**
   * 단일 요소 재바인딩
   */
  rebindElement(element) {
    this.unbindElement(element)
    this.bindElement(element)
  }

  /**
   * 단일 요소 바인딩
   */
  bindElement(element) {
    const actionValue = element.dataset.makaseAction
    if (!actionValue) return

    // 이미 바인딩되어 있으면 언바인딩 먼저
    if (this.handlerMap.has(element)) {
      this.unbindElement(element)
    }

    // Map<eventType, Function[]>
    const handlersMap = new Map()

    // 액션 문자열 파싱 (멀티라인 지원)
    const actions = this.parseActionString(actionValue)

    for (const action of actions) {
      try {
        const parsed = this.parseAction(action)
        if (!parsed) continue

        const { eventType, handler } = parsed

        // 같은 이벤트 타입에 여러 핸들러 등록 가능
        if (!handlersMap.has(eventType)) {
          handlersMap.set(eventType, [])
        }
        handlersMap.get(eventType).push(handler)
        element.addEventListener(eventType, handler)
      } catch (error) {
        console.error(`[makase] 파싱 오류 (action: "${action.substring(0, 50)}..."):`, error.message)
      }
    }

    this.handlerMap.set(element, handlersMap)
  }

  /**
   * 단일 요소 언바인딩
   */
  unbindElement(element) {
    const handlersMap = this.handlerMap.get(element)
    if (!handlersMap) return

    for (const [eventType, handlers] of handlersMap) {
      for (const handler of handlers) {
        element.removeEventListener(eventType, handler)
      }
    }

    this.handlerMap.delete(element)
  }

  /**
   * 액션 문자열을 개별 액션으로 파싱 (멀티라인 코드 블록 지원)
   * @param {string} actionString - data-makase-action 값
   * @returns {string[]} 개별 액션 배열
   */
  parseActionString(actionString) {
    const actions = []
    let current = ""
    let braceDepth = 0
    let i = 0

    while (i < actionString.length) {
      const char = actionString[i]

      if (char === "{") {
        braceDepth++
        current += char
      } else if (char === "}") {
        braceDepth--
        current += char

        // 브레이스가 닫히면 하나의 액션 완료
        if (braceDepth === 0) {
          const trimmed = current.trim()
          if (trimmed && !trimmed.startsWith("//")) {
            actions.push(trimmed)
          }
          current = ""
        }
      } else if (char === "\n" && braceDepth === 0) {
        // 브레이스 밖에서 줄바꿈이면 액션 구분
        const trimmed = current.trim()
        if (trimmed && !trimmed.startsWith("//")) {
          actions.push(trimmed)
        }
        current = ""
      } else {
        current += char
      }

      i++
    }

    // 마지막 남은 액션
    const trimmed = current.trim()
    if (trimmed && !trimmed.startsWith("//")) {
      actions.push(trimmed)
    }

    return actions
  }

  /**
   * 개별 액션 파싱
   * @param {string} action - 개별 액션 문자열
   * @returns {{ eventType: string, handler: Function } | null}
   */
  parseAction(action) {
    // 주석 무시
    if (action.startsWith("//")) return null

    // 패턴: eventname->{ ... }
    // 멀티라인 지원을 위해 [\s\S] 사용
    const match = action.match(/^(\w+(?::\w+)?)\s*->\s*\{([\s\S]*)\}\s*$/)
    if (match) {
      const [, eventType, code] = match
      return {
        eventType,
        handler: this.createHandler(code),
      }
    }

    throw new Error(`인식할 수 없는 문법: "${action}"`)
  }

  /**
   * JavaScript 핸들러 생성
   * @param {string} code - 실행할 JavaScript 코드
   */
  createHandler(code) {
    const controller = this

    return (event) => {
      try {
        const $el = event.currentTarget

        // input, select, textarea의 경우 value 제공
        const hasValue = $el.tagName === "INPUT" ||
          $el.tagName === "SELECT" ||
          $el.tagName === "TEXTAREA"
        const value = hasValue ? $el.value : undefined

        // 헬퍼 함수들
        const $select = (selector, callback) => selectElement(selector, callback)
        const $selects = (selector, callback) => selectElements(selector, callback)
        const $emit = (eventName, detail, bubbles = true) =>
          dispatchElement(controller.element, eventName, detail, bubbles)
        const $dispatch = (selector, eventName, detail, bubbles = true) =>
          dispatchElement(selector, eventName, detail, bubbles)
        const $handle = (selector, eventName, detail) =>
          handleElement(selector, eventName, detail)

        // $ref, $refs - data-makase-ref 속성을 가진 요소를 찾는 헬퍼
        const buildRefSelector = (refArg) => {
          // 문자열인 경우: $ref('target') -> [controller.element, '[data-makase-ref~=target]']
          if (typeof refArg === "string") {
            return [controller.element, `[data-makase-ref~="${refArg}"]`]
          }
          // 배열인 경우: $ref([el, 'li', 'target']) -> [controller.element, el, 'li', '[data-makase-ref~=target]']
          if (Array.isArray(refArg)) {
            const lastItem = refArg[refArg.length - 1]
            const restItems = refArg.slice(0, -1)
            // 마지막 항목이 문자열이면 ref 이름으로 간주
            if (typeof lastItem === "string") {
              return [controller.element, ...restItems, `[data-makase-ref~="${lastItem}"]`]
            }
            // 마지막 항목이 요소면 그대로 반환
            return [controller.element, ...refArg]
          }
          // HTMLElement인 경우: $ref(el) -> data-makase-ref가 있으면 해당 요소
          if (refArg instanceof HTMLElement) {
            if (typeof refArg.dataset.makaseRef === "string") {
              return [controller.element, refArg]
            }
            return refArg
          }
          return refArg
        }
        const $ref = (refArg, callback) => selectElement(buildRefSelector(refArg), callback)
        const $refs = (refArg, callback) => selectElements(buildRefSelector(refArg), callback)

        // Function 생성 - 모든 변수를 주입
        const fn = new Function(
          // 이벤트 관련
          "event", "$event",
          // 요소 관련
          "$el",
          // value (input 등)
          "value", "$value",
          // 컨트롤러
          "controller", "$controller",
          // 헬퍼 함수
          "$select", "$selects", "$emit", "$dispatch", "$handle", "$ref", "$refs",
          // 네이티브 (명시적으로 접근 가능하도록)
          `
          "use strict";
          ${code}
          `
        )

        fn(
          // 이벤트 관련
          event, event,
          // 요소 관련
          $el,
          // value
          value, value,
          // 컨트롤러
          controller, controller,
          // 헬퍼 함수
          $select, $selects, $emit, $dispatch, $handle, $ref, $refs
        )
      } catch (error) {
        console.error(`[makase] 코드 실행 오류:`, error)
      }
    }
  }

  /**
   * 컨트롤러에 이벤트 액션을 등록하여 makase의 $handle로 호출 가능하게 함
   *
   * 사용법:
   * ```js
   * import MakaseController from "./makase_controller"
   *
   * class FooController extends Controller {
   *   connect() {
   *     MakaseController.useEventAction(this, "console")  // "->foo#console" 로 이벤트가 등록됨
   *     MakaseController.useEventAction(this, "reload")   // "->foo#reload" 로 이벤트가 등록됨
   *   }
   * }
   * ```
   *
   * @param {Controller} controller - Stimulus 컨트롤러 인스턴스
   * @param {string} methodName - 이벤트로 등록할 메서드 이름
   * @param {Object} options - 옵션
   * @param {string} options.domain - {도메인}-> (기본값: "")
   */
  static useEventAction(controller, methodName, options = {}) {
    const controllerName = controller.identifier
    const domainName = options?.domain || ""
    const eventName = `${domainName}->${controllerName}#${methodName}`
    const handler = (event) => {
      event.stopPropagation()

      const method = controller[methodName]
      if (typeof method === "function") {
        method.call(controller, event)
      } else {
        console.warn(`[useEventAction] "${controllerName}#${methodName}" 메서드를 찾을 수 없습니다.`)
      }
    }

    controller.element.addEventListener(eventName, handler)

    // disconnect 시 자동 해제를 위해 저장
    if (!controller._eventActionHandlers) {
      controller._eventActionHandlers = []

      // 원본 disconnect를 래핑
      const originalDisconnect = controller.disconnect?.bind(controller)
      controller.disconnect = function () {
        // 등록된 핸들러들 해제
        for (const { eventName, handler } of this._eventActionHandlers) {
          this.element.removeEventListener(eventName, handler)
        }
        this._eventActionHandlers = []

        // 원본 disconnect 호출
        if (originalDisconnect) {
          originalDisconnect()
        }
      }
    }

    controller._eventActionHandlers.push({ eventName, handler })

    return { eventName, handler }
  }

  /**
   * 여러 메서드를 한 번에 이벤트 액션으로 등록
   *
   * 사용법:
   * ```js
   * import MakaseController from "./makase_controller"
   *
   * class FooController extends Controller {
   *   connect() {
   *     MakaseController.useEventActions(this, ["console", "reload", "save"])
   *   }
   * }
   * ```
   *
   * @param {Controller} controller - Stimulus 컨트롤러 인스턴스
   * @param {string[]} methodNames - 이벤트로 등록할 메서드 이름들
   */
  static useEventActions(controller, methodNames) {
    for (const methodName of methodNames) {
      MakaseController.useEventAction(controller, methodName)
    }
  }
}
